import type { Organization } from '@services/customer.service';

// 26b — the row shape the organisation register renders, and the only place
// that knows how the API's response maps onto it.
//
// This exists for one reason: the frontend's `Organization` interface is wider
// than the deployed record. It declares `tier`, `status`, `parentId`,
// `children`, `phone`, `address`, `ticketCount` and `openTicketCount`, none of
// which are columns on the `organizations` table and none of which any
// organisations endpoint sets. Rendering straight off that interface is how a
// register ends up with four permanently empty columns and nobody able to say
// why.
//
// `OrgRow` is therefore deliberately narrow: every field on it is one the list
// endpoint actually returns. If a column is not here, the data is not there.

/** Sort keys. Every one is a REAL column on `organizations`. */
export type OrgSort = 'name' | 'industry' | 'createdAt';

/**
 * Whitelisted because customer-service builds its Prisma `orderBy` as
 * `{ [sortBy]: sortOrder }` with no validation — an unknown field is a 500,
 * and a bookmarked `?sortBy=seats` must never reach it.
 *
 * `people` is NOT here on purpose: it is `_count.customers`, an aggregate, and
 * `{ customers: 'desc' }` is not a valid Prisma ordering.
 */
export const ORG_SORTS: { id: OrgSort; label: string }[] = [
  { id: 'createdAt', label: 'Date added' },
  { id: 'name', label: 'Name' },
  { id: 'industry', label: 'Industry' },
];

export const ORG_SORT_IDS = new Set<OrgSort>(ORG_SORTS.map((s) => s.id));

export interface OrgRow {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
  /** A BAND ("51-200"), not a headcount. Rendered as text, never as a number. */
  size: string | null;
  tags: string[];
  /** `_count.customers` — customer records pointing at this organisation. */
  people: number;
  createdAt: string;
}

/**
 * The list endpoint returns Prisma's `_count: { customers }` and does not map
 * it to `customerCount`, so both spellings are read: whichever the deployed
 * build happens to send, the column fills. A missing count is 0 rather than
 * `undefined` because the register's one derived fact — "nothing points at
 * this account" — has to be decidable.
 */
export function toOrgRow(org: Organization): OrgRow {
  const counted = (org as { _count?: { customers?: number } })._count?.customers;
  const people = typeof counted === 'number' ? counted : (org.customerCount ?? 0);

  const trimmed = (v: string | undefined | null) => {
    const s = (v ?? '').trim();
    return s.length > 0 ? s : null;
  };

  return {
    id: org.id,
    name: org.name,
    domain: trimmed(org.domain),
    industry: trimmed(org.industry),
    size: trimmed(org.size),
    tags: org.tags ?? [],
    people,
    createdAt: org.createdAt,
  };
}
